import { CompanyWorkerSalary } from "model/Salary";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useMonthSalaryStore } from "web-apps/company/stores/monthSalaryStore/monthSalaryStore";
import { Pagination } from "../general/pagination.component";
import { WorkerProfile } from "../organizationalStructure/workerProfile.component";
import { MonthSalaryBody } from "./salary.styled";
import { SalaryListRow } from "./salaryListRow.component";
import { UpdateWorkerSalaryModal } from "./updateWorkerSalaryModal.component";
import {
  ColumnListWrapper,
  EmptyState,
} from "../applications/applicationList.styled";

type Props = {
  selectedWorker?: number;
};

export const SalaryList: React.FC<Props> = ({ selectedWorker }) => {
  const { t } = useTranslation();
  const [{ company, companyWorkerImages }] = useCompanyStore();
  const [state, monthSalaryActions] = useMonthSalaryStore();

  const [page, setPage] = useState(0);
  const [clickedWorker, setClickedWorker] = useState<number | undefined>(
    undefined,
  );
  const [showWorkerProfile, setShowWorkerProfile] = useState(false);
  const [showEditWorkerSalaryModal, setShowEditWorkerSalaryModal] =
    useState(false);
  const [selectedWorkerWithSalary, setSelectedWorkerWithSalary] =
    useState<CompanyWorkerSalary>();

  const pageSize = 25;

  useEffect(() => {
    if (!company?.id) return;
    monthSalaryActions.getAllSalaries(company.id, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, page]);

  const displayedWorkers = selectedWorker
    ? state.workersWithSalary.filter((w) => w.workerId === selectedWorker)
    : state.workersWithSalary;

  const totalPages = Math.ceil(state.total / pageSize);

  const handleOpenProfile = (workerId: number): void => {
    setClickedWorker(workerId);
    setShowWorkerProfile(true);
  };

  const handleOpenEdit = (salary: CompanyWorkerSalary): void => {
    setSelectedWorkerWithSalary(salary);
    setShowEditWorkerSalaryModal(true);
  };

  const renderSalaries = (): JSX.Element => {
    if (displayedWorkers.length === 0) {
      return <EmptyState typography="b1">{t("Salary.NoSalaries")}</EmptyState>;
    }

    return (
      <MonthSalaryBody>
        {displayedWorkers.map((salary) => (
          <SalaryListRow
            key={`worker-${salary.workerId}`}
            salary={salary}
            avatarUrl={
              salary.externalId
                ? companyWorkerImages[salary.externalId]
                : undefined
            }
            onOpenProfile={handleOpenProfile}
            onOpenEdit={handleOpenEdit}
          />
        ))}
      </MonthSalaryBody>
    );
  };

  return (
    <>
      <ColumnListWrapper>
        {renderSalaries()}
        {totalPages > 1 && (
          <Pagination page={page} setPage={setPage} totalPages={totalPages} />
        )}
      </ColumnListWrapper>

      {selectedWorkerWithSalary && (
        <UpdateWorkerSalaryModal
          showUpdateWorkerSalaryModal={showEditWorkerSalaryModal}
          setShowUpdateWorkerSalaryModal={setShowEditWorkerSalaryModal}
          selectedWorkerWithSalary={selectedWorkerWithSalary}
          currentPage={page}
          pageSize={pageSize}
        />
      )}
      {clickedWorker && (
        <WorkerProfile
          worker={clickedWorker}
          showWorkerProfile={showWorkerProfile}
          setShowWorkerProfile={setShowWorkerProfile}
          setClickedWorker={setClickedWorker}
        />
      )}
    </>
  );
};
